import { authHeaders, fetchWithAuthRetry, getApiUrl, parseApiError } from './common';

export type ServiceTokenScope = 'read' | 'write';

export type ServiceToken = {
  id: string;
  projectId: string;
  name: string;
  scope: ServiceTokenScope;
  expiresAt: string;
  revokedAt: string | null;
  lastUsedAt: string | null;
  createdAt: string;
};

export type IssuedServiceToken = {
  token: string;
  serviceToken: ServiceToken;
};

export const fetchServiceTokens = async (token: string, projectId: string): Promise<ServiceToken[]> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/service-tokens`, {
    cache: 'no-store',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to fetch service tokens: ${response.status}`);
  }

  return ((await response.json()) as { data: ServiceToken[] }).data;
};

export const issueServiceToken = async (
  token: string,
  projectId: string,
  input: { name: string; scope: ServiceTokenScope; expiresAt: string },
): Promise<IssuedServiceToken> => {
  const response = await fetchWithAuthRetry(`${getApiUrl()}/api/projects/${projectId}/service-tokens`, {
    method: 'POST',
    headers: authHeaders(token, true),
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    throw await parseApiError(response, `Failed to issue service token: ${response.status}`);
  }

  return ((await response.json()) as { data: IssuedServiceToken }).data;
};

export const revokeServiceToken = async (token: string, projectId: string, serviceTokenId: string): Promise<void> => {
  const response = await fetchWithAuthRetry(
    `${getApiUrl()}/api/projects/${projectId}/service-tokens/${serviceTokenId}`,
    {
      method: 'DELETE',
      headers: authHeaders(token),
    },
  );

  if (!response.ok) {
    throw await parseApiError(response, `Failed to revoke service token: ${response.status}`);
  }
};
